/**
 * Wizard Layout
 *
 * Gemensam layout för alla wizard-steg
 *
 * K2-LÄGE (inloggad, 5 steg):
 * Steg 1: Bokföringsdata → tillhandahåll data
 * Steg 2: Översikt → granska & validera
 * Steg 3: Komplettera → fyll i uppgifter
 * Steg 4: Disposition → EK-förändringar & utdelning
 * Steg 5: Exportera → förhandsgranska & ladda ner
 *
 * K3-LÄGE (inloggad, 6 steg):
 * Steg 1: Bokföringsdata → tillhandahåll data
 * Steg 2: Översikt → granska & validera
 * Steg 3: Kassaflödesanalys → K3-obligatorisk
 * Steg 4: Komplettera → fyll i uppgifter (inkl. uppskjuten skatt)
 * Steg 5: Disposition → EK-förändringar & utdelning
 * Steg 6: Exportera → förhandsgranska & ladda ner
 *
 * Gäst-läge (K2): Steg 1 = Företag (definiera företag), sedan som ovan
 */

import { ReactNode, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowLeft, Lock, Clock, RotateCcw, X, AlertTriangle, Loader2 } from "lucide-react";
import { configApi } from "@/lib/api";
import type { Company, AnnualReport, AccountingStandard } from "@/lib/api";

interface WizardStep {
  id: string; // URL step identifier (e.g., "1", "2", "2.5", "3")
  displayNumber: number; // Display number in progress bar
  title: string;
  description: string;
}

function getWizardSteps(isGuestMode: boolean, accountingStandard: AccountingStandard): WizardStep[] {
  if (isGuestMode) {
    // Guest mode: 6-7 steps depending on K2/K3 - starts with company definition
    if (accountingStandard === "K3") {
      return [
        { id: "1", displayNumber: 1, title: "Företag", description: "Definiera företag" },
        { id: "2", displayNumber: 2, title: "Bokföringsdata", description: "Tillhandahåll data" },
        { id: "3", displayNumber: 3, title: "Översikt", description: "Granska & validera" },
        { id: "3.5", displayNumber: 4, title: "Kassaflöde", description: "K3 obligatorisk" },
        { id: "4", displayNumber: 5, title: "Komplettera", description: "Fyll i uppgifter" },
        { id: "5", displayNumber: 6, title: "Disposition", description: "EK & utdelning" },
        { id: "6", displayNumber: 7, title: "Exportera", description: "Skapa konto & exportera" },
      ];
    }
    return [
      { id: "1", displayNumber: 1, title: "Företag", description: "Definiera företag" },
      { id: "2", displayNumber: 2, title: "Bokföringsdata", description: "Tillhandahåll data" },
      { id: "3", displayNumber: 3, title: "Översikt", description: "Granska & validera" },
      { id: "4", displayNumber: 4, title: "Komplettera", description: "Fyll i uppgifter" },
      { id: "5", displayNumber: 5, title: "Disposition", description: "EK & utdelning" },
      { id: "6", displayNumber: 6, title: "Exportera", description: "Skapa konto & exportera" },
    ];
  }

  // Logged-in mode - K3 has 6 steps with cash flow
  if (accountingStandard === "K3") {
    return [
      { id: "1", displayNumber: 1, title: "Bokföringsdata", description: "Tillhandahåll data" },
      { id: "2", displayNumber: 2, title: "Översikt", description: "Granska & validera" },
      { id: "2.5", displayNumber: 3, title: "Kassaflöde", description: "K3 obligatorisk" },
      { id: "3", displayNumber: 4, title: "Komplettera", description: "Inkl. uppskjuten skatt" },
      { id: "4", displayNumber: 5, title: "Disposition", description: "EK & utdelning" },
      { id: "5", displayNumber: 6, title: "Exportera", description: "Förhandsgranska & ladda ner" },
    ];
  }

  // Logged-in K2 mode: 5 steps
  return [
    { id: "1", displayNumber: 1, title: "Bokföringsdata", description: "Tillhandahåll data" },
    { id: "2", displayNumber: 2, title: "Översikt", description: "Granska & validera" },
    { id: "3", displayNumber: 3, title: "Komplettera", description: "Fyll i uppgifter" },
    { id: "4", displayNumber: 4, title: "Disposition", description: "EK & utdelning" },
    { id: "5", displayNumber: 5, title: "Exportera", description: "Förhandsgranska & ladda ner" },
  ];
}

interface WizardLayoutProps {
  company: Company;
  report: AnnualReport;
  /** Current step - supports string ("1", "2", "2.5") or number (1, 2, 3) for backward compat */
  currentStep: string | number;
  /** Display step number for header (e.g., 3 for step "2.5" in K3) */
  displayStepNumber?: number;
  /** Total steps for this accounting standard */
  totalSteps?: number;
  /** Accounting standard (K2 or K3) */
  accountingStandard?: AccountingStandard;
  children: ReactNode;
  /** Guest mode: adjust back link and step navigation */
  guestMode?: boolean;
  /**
   * Override step navigation (for guest/public wizard)
   * In guest mode, receives displayNumber (number)
   * In logged-in mode, receives step id (string like "1", "2.5", "3")
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNavigateStep?: (step: any) => void;
  /** Reset handler (for guest/public wizard) */
  onReset?: () => void;
  /** Callback to change accounting standard (shows confirmation dialog) */
  onChangeStandard?: (newStandard: AccountingStandard) => Promise<void>;
}

export function WizardLayout({
  company,
  report,
  currentStep: rawCurrentStep,
  displayStepNumber,
  totalSteps,
  accountingStandard = "K2",
  children,
  guestMode,
  onNavigateStep,
  onReset,
  onChangeStandard,
}: WizardLayoutProps) {
  const navigate = useNavigate();
  const fiscalYear = `${report.fiscalYearStart.slice(0, 4)}/${report.fiscalYearEnd.slice(0, 4)}`;

  // Check if user has actively confirmed accounting standard choice
  const isStandardConfirmed = !!(report.userInput as Record<string, unknown> | null)?.accountingStandardConfirmed;

  // Normalize currentStep to string for comparison
  const currentStep = String(rawCurrentStep);

  // Highest step reached (from database) - convert to string for comparison
  const highestStepReached = String(report.currentStep || 1);

  // Get wizard steps based on accounting standard
  const wizardSteps = getWizardSteps(!!guestMode, accountingStandard);

  // Calculate display step number if not provided
  const effectiveDisplayStep = displayStepNumber ?? (
    wizardSteps.find(s => s.id === currentStep)?.displayNumber ?? parseFloat(currentStep)
  );
  const effectiveTotalSteps = totalSteps ?? wizardSteps.length;

  // Standard change confirmation dialog
  const [showChangeStandardDialog, setShowChangeStandardDialog] = useState(false);
  const [isChangingStandard, setIsChangingStandard] = useState(false);
  const newStandard: AccountingStandard = accountingStandard === "K3" ? "K2" : "K3";

  // Dynamic edit window from admin settings
  const [editWindowDays, setEditWindowDays] = useState(30);
  useEffect(() => {
    configApi.getPublic().then((res) => {
      if (res.success && res.data) setEditWindowDays(res.data.editWindowDays);
    }).catch(() => {});
  }, []);

  // Check if report is finalized (permanently locked)
  const isFinalized = report.status === "finalized";

  // Edit window calculation (only for completed/exported, not finalized)
  const editWindowInfo = (() => {
    // Finalized reports are always locked
    if (isFinalized) {
      return { locked: true, remainingDays: 0, isFinalized: true };
    }
    if (report.status !== "completed" && report.status !== "exported") return null;
    if (editWindowDays <= 0) return null;
    // Use completedAt (when "Markera som slutförd" was clicked) as the anchor
    // Fall back to exportedAt for exported reports, then updatedAt for older reports
    const anchor = report.completedAt
      ? new Date(report.completedAt)
      : report.exportedAt
        ? new Date(report.exportedAt)
        : new Date(report.updatedAt);
    const lockDate = new Date(anchor.getTime() + editWindowDays * 24 * 60 * 60 * 1000);
    const remaining = Math.ceil((lockDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    return { locked: remaining <= 0, remainingDays: Math.max(0, remaining), isFinalized: false };
  })();

  // Report is locked if finalized or edit window has expired
  const isLocked = isFinalized || (editWindowInfo?.locked ?? false);

  // Get previous step in wizard sequence (or null if on first step)
  const currentStepIndex = wizardSteps.findIndex(s => s.id === currentStep);
  const previousStep = currentStepIndex > 0 ? wizardSteps[currentStepIndex - 1] : null;

  // Handle back button click - navigate to previous step or exit wizard
  const handleBackClick = () => {
    if (previousStep) {
      // Navigate to previous step in wizard
      if (onNavigateStep) {
        // Use callback for guest mode (passes displayNumber for guest, step id for logged-in)
        onNavigateStep(guestMode ? previousStep.displayNumber : previousStep.id);
      } else {
        // Direct navigation for logged-in mode
        navigate(`/dashboard/wizard/${report.id}/step/${previousStep.id}`);
      }
    } else {
      // On first step - exit wizard
      navigate(guestMode ? "/" : `/dashboard/companies/${company.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Left side: Back + Company info */}
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={handleBackClick}
                className="flex items-center text-gray-600 hover:text-gray-900 p-1.5 -ml-1.5 rounded-md hover:bg-gray-100 flex-shrink-0"
                title="Tillbaka"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline ml-2">Tillbaka</span>
              </button>
              <div className="h-6 w-px bg-gray-300 hidden sm:block flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{company.name}</h1>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  Årsredovisning {fiscalYear}
                </p>
              </div>
            </div>

            {/* Right side: Actions + Step info */}
            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              {guestMode && onReset && (
                <button
                  onClick={onReset}
                  className="group flex items-center justify-center gap-1.5 p-2 sm:px-2.5 sm:py-1.5 text-sm text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100/80 transition-all duration-150"
                  title="Börja om"
                >
                  <RotateCcw size={16} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                  <span className="hidden sm:inline opacity-80 group-hover:opacity-100">Börja om</span>
                </button>
              )}
              <button
                onClick={() => {
                  navigate(guestMode ? "/" : `/dashboard/companies/${company.id}`);
                }}
                className="group flex items-center justify-center gap-1.5 p-2 sm:px-2.5 sm:py-1.5 text-sm text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100/80 transition-all duration-150"
                title="Stäng"
              >
                <X size={16} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                <span className="hidden sm:inline opacity-80 group-hover:opacity-100">Stäng</span>
              </button>
              {/* Step indicator - compact on mobile */}
              <div className="flex items-center gap-1 sm:gap-2 pl-1 sm:pl-2 border-l border-gray-200">
                <span className="text-xs sm:text-sm text-gray-500">
                  <span className="sm:hidden">{effectiveDisplayStep}/{effectiveTotalSteps}</span>
                  <span className="hidden sm:inline">Steg {effectiveDisplayStep} av {effectiveTotalSteps}</span>
                </span>
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                  accountingStandard === "K3"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600"
                }`}>
                  {accountingStandard}
                  {/* Only show (byt) after user has actively confirmed their standard choice */}
                  {onChangeStandard && !isLocked && isStandardConfirmed && (
                    <button
                      onClick={() => setShowChangeStandardDialog(true)}
                      className="ml-1 text-xs opacity-70 hover:opacity-100 hover:underline hidden sm:inline"
                    >
                      (byt)
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 overflow-x-auto scrollbar-hide">
          <nav className="flex justify-between min-w-max sm:min-w-0">
            {wizardSteps.map((step, index) => {
              // Compare steps by their index in the wizardSteps array
              const currentStepIndex = wizardSteps.findIndex(s => s.id === currentStep);
              const stepIndex = index;
              const highestStepIndex = wizardSteps.findIndex(s => s.id === highestStepReached);

              const isCompleted = stepIndex < currentStepIndex;
              const isCurrent = step.id === currentStep;
              // Step is accessible if it's been reached before
              const isAccessible = stepIndex <= Math.max(highestStepIndex, 0);
              // If report is locked, only the last step is clickable
              const isClickable = isLocked
                ? index === wizardSteps.length - 1 && !isCurrent
                : isAccessible && !isCurrent;

              const handleStepClick = () => {
                if (isClickable) {
                  if (onNavigateStep) {
                    // In guest mode, pass displayNumber (number) for backward compatibility
                    // In regular mode, pass step id (string) for K3 support
                    onNavigateStep(guestMode ? step.displayNumber : step.id);
                  } else {
                    navigate(`/dashboard/wizard/${report.id}/step/${step.id}`);
                  }
                }
              };

              return (
                <div
                  key={step.id}
                  className={`flex items-center ${
                    index < wizardSteps.length - 1 ? "flex-1" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={handleStepClick}
                    disabled={!isClickable}
                    className={`flex items-center text-left ${
                      isClickable
                        ? "cursor-pointer group"
                        : isCurrent
                        ? "cursor-default"
                        : "cursor-not-allowed"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                        isCompleted
                          ? "bg-green-500 border-green-500 text-white"
                          : isCurrent
                          ? "bg-primary-600 border-primary-600 text-white"
                          : isAccessible
                          ? "bg-white border-gray-400 text-gray-600"
                          : "bg-white border-gray-300 text-gray-400"
                      } ${
                        isClickable
                          ? "group-hover:scale-110 group-hover:shadow-md"
                          : ""
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle size={20} />
                      ) : (
                        <span className="font-medium">{step.displayNumber}</span>
                      )}
                    </div>
                    <div className="ml-3 hidden md:block">
                      <p
                        className={`text-sm font-medium transition-colors ${
                          isAccessible ? "text-gray-900" : "text-gray-400"
                        } ${isClickable ? "group-hover:text-primary-600" : ""}`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </button>

                  {index < wizardSteps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-4 ${
                        stepIndex < Math.max(highestStepIndex, 0)
                          ? "bg-green-500"
                          : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Edit window / lock status banner */}
      {editWindowInfo && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          {editWindowInfo.isFinalized ? (
            <div className="flex items-center gap-2 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
              <Lock size={16} className="text-primary-500 shrink-0" />
              <span>
                Denna årsredovisning har <strong>skickats till Bolagsverket</strong> och är permanent låst.
                Inga ändringar kan göras.
              </span>
            </div>
          ) : editWindowInfo.locked ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              <Lock size={16} className="text-gray-400 shrink-0" />
              <span>
                Denna årsredovisning är <strong>låst</strong> och kan inte längre redigeras.
                Kontakta admin om du behöver göra ändringar.
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <Clock size={16} className="text-amber-500 shrink-0" />
              <span>
                Du har <strong>{editWindowInfo.remainingDays} {editWindowInfo.remainingDays === 1 ? "dag" : "dagar"}</strong> kvar
                att göra ändringar. Därefter låses årsredovisningen permanent.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Change Standard Confirmation Dialog */}
      {showChangeStandardDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => !isChangingStandard && setShowChangeStandardDialog(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="text-amber-600" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Byt till {newStandard}?
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {accountingStandard === "K3" ? (
                    <>
                      Om du byter från <strong>K3</strong> till <strong>K2</strong> kommer
                      kassaflödesanalysen och eventuella K3-specifika uppgifter att tas bort.
                    </>
                  ) : (
                    <>
                      Om du byter från <strong>K2</strong> till <strong>K3</strong> kommer
                      du behöva fylla i kassaflödesanalys och andra K3-specifika uppgifter.
                    </>
                  )}
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  Du kommer att tas tillbaka till översikten för att fortsätta därifrån.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowChangeStandardDialog(false)}
                    className="btn btn-secondary"
                    disabled={isChangingStandard}
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={async () => {
                      if (!onChangeStandard) return;
                      setIsChangingStandard(true);
                      try {
                        await onChangeStandard(newStandard);
                        setShowChangeStandardDialog(false);
                      } finally {
                        setIsChangingStandard(false);
                      }
                    }}
                    className="btn btn-primary flex items-center gap-2"
                    disabled={isChangingStandard}
                  >
                    {isChangingStandard ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Byter...
                      </>
                    ) : (
                      `Byt till ${newStandard}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
